import { Object3D, Vector3 } from "three";
import IDefaultHoverListener from "./IDefaultHoverListener";
import RaycastIntersection from "../logic/RaycastIntersection";
import { IRoomLabelHoverTarget, getRoomLabelHoverTarget, getRoomNoteTarget } from "../interfaces/IRoomNoteTarget";
import { useHoveredRoomNoteStore } from "../store/hoveredRoomNoteStore";

/**
 * Handles pointer moves that no command consumed over room labels:
 * - over a note icon, shows the room note as a tooltip
 * - over any part of a label, tells the label it is hovered, so it can reveal its add-note icon
 * Looks through all hits because labels are drawn on top of the model, so the label is often not the nearest hit.
 */
export default class RoomNoteHoverListener implements IDefaultHoverListener {

    #hoveredIcon: Object3D | null = null;
    #hoveredLabel: IRoomLabelHoverTarget | null = null;

    getName(): string {
        return "RoomNoteHoverListener";
    }

    defaultPointerMove(raycastResults: RaycastIntersection[]): boolean {
        const labelConsumed = this.updateHoveredLabel(raycastResults);
        const iconConsumed = this.updateHoveredIcon(raycastResults);
        return labelConsumed || iconConsumed;
    }

    private updateHoveredLabel(raycastResults: RaycastIntersection[]): boolean {
        const label = raycastResults
            .map(r => getRoomLabelHoverTarget(r.object))
            .find(t => t !== undefined) ?? null;

        if (label !== this.#hoveredLabel) {
            this.#hoveredLabel?.setHovered(false);
            label?.setHovered(true);
            this.#hoveredLabel = label;
        }
        return label !== null;
    }

    private updateHoveredIcon(raycastResults: RaycastIntersection[]): boolean {
        // Only icons of rooms that have a note show a tooltip
        const hit = raycastResults.find(r => getRoomNoteTarget(r.object)?.note);
        const icon = hit?.object ?? null;

        if (icon === this.#hoveredIcon) return icon !== null; // Unchanged, avoid store updates on every move
        this.#hoveredIcon = icon;

        if (!icon) {
            useHoveredRoomNoteStore.getState().clearHoveredRoomNote();
            return false;
        }

        // Anchored to the icon, not the cursor, so the tooltip stays put while the pointer moves over the icon
        const target = getRoomNoteTarget(icon)!;
        useHoveredRoomNoteStore.getState().setHoveredRoomNote(target.note!, icon.getWorldPosition(new Vector3()));
        return true;
    }
}
